import type { HTMLAttributes, ReactElement, ReactNode, Ref } from 'react'
import { Children, cloneElement, isValidElement } from 'react'
import { FloatingPortal, useMergeRefs } from '@floating-ui/react'
import { useTooltip } from '#hooks/use-tooltip.ts'
import type { UseTooltipOptions } from '#hooks/use-tooltip.ts'
import { floatingRoot } from '#core/utils/floating-root.ts'
import { cn } from '#core/utils/cn.ts'
import { TooltipPanel } from '#ynt-design-system/components/tooltip/tooltip-panel.tsx'
import type { TooltipPanelProps } from '#ynt-design-system/components/tooltip/tooltip-panel.tsx'

export interface TooltipProps extends Omit<TooltipPanelProps, 'ref' | 'children'>, UseTooltipOptions {
  /**
   * The element the tooltip describes. A single element receives the handlers and
   * `aria-describedby` directly, so it must accept a `ref`; anything else is wrapped
   * in a span that takes them instead.
   */
  children: ReactNode
}

type TriggerElement = ReactElement<HTMLAttributes<HTMLElement> & { ref?: Ref<HTMLElement> }>

/**
 * A hover/focus tooltip: a label, optional dimmed secondary text, and optional value
 * rows carrying an icon, a series indicator and a trailing amount. The panel is
 * portaled and positioned by {@link useTooltip}. Reach for {@link TooltipPanel} when
 * something else owns placement, and for the hook when something else owns the panel.
 */
export function Tooltip({
  placement,
  align,
  openDelay,
  closeDelay,
  disabled,
  open,
  onOpenChange,
  children,
  className,
  style,
  ...panel
}: TooltipProps) {
  const tooltip = useTooltip({ placement, align, openDelay, closeDelay, disabled, open, onOpenChange })
  const child = isValidElement(children) ? (Children.only(children) as TriggerElement) : null
  const { ref: childRef, ...childProps } = child?.props ?? {}
  const triggerRef = useMergeRefs([tooltip.refs.setReference, childRef])

  const trigger = child ? (
    cloneElement(child, { ...tooltip.getReferenceProps(childProps), ref: triggerRef })
  ) : (
    <span ref={triggerRef} className="ynt-tooltip-trigger" {...tooltip.getReferenceProps()}>
      {children}
    </span>
  )

  return (
    <>
      {trigger}
      {tooltip.open && (
        <FloatingPortal root={floatingRoot(tooltip.context.elements.domReference)}>
          <TooltipPanel
            {...panel}
            {...tooltip.getFloatingProps()}
            ref={tooltip.refs.setFloating}
            floating
            className={cn(tooltip.instant && 'ynt-tooltip-instant', className)}
            style={{ ...tooltip.floatingStyles, ...style }}
          />
        </FloatingPortal>
      )}
    </>
  )
}

export default Tooltip
